import className from "classnames";
import { Immutable } from "immer";
import React from "react";

import {
    IOptionSingleValue,
    IOptionValues,
    IProduct,
} from "../../../models/catalogue";
import { isoImageURL } from "../../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { PLCInfoPanel } from "../PLCInfoPanel";
import { defaults } from "../defaults";
import {
    IPLCOptionPanel,
    IPLCOtherOptionSelector,
    IPLCProductCategorySelector,
} from "../models";
import { getMostSimilarProduct } from "../product-similarity";
import { setOptionValue } from "../reducers";
import { ISelectedCategories } from "../reducers.interfaces";
import { selectedCategoriesSelector } from "../selectors";
import { getOptionValueByIdx } from "../utils";

// Load styles
import "./index.scss";

const BEM_NAME = "plc-other-option-selector";

/**
 * Check if selecting this option will result in a perfect product match or not. If
 * not, the option should be visually disabled (but still actually selectable).
 */
const isOptionDisabled = (
    option: IOptionSingleValue,
    categorySelectors: readonly IPLCProductCategorySelector[],
    optionPanels: readonly IPLCOptionPanel[],
    rootProducts: readonly IProduct[],
    selectedCategories: ISelectedCategories,
    optionValues: IOptionValues,
    attribute: string,
): boolean => {
    const futureOptionValues = {
        ...optionValues,
        [attribute]: option,
    };
    const result = getMostSimilarProduct({
        categorySelectors,
        optionPanels,
        rootProducts,
        selectedCategories,
        optionValues: futureOptionValues,
        previouslySelectedCategories: selectedCategories,
        previouslySelectedOptionValues: optionValues,
    });
    return !result || result.distance > 0;
};

const generateOptionID = (groupID: string, optionValue: string): string => {
    return `${groupID}-${optionValue}`
        .replace(/[^a-zA-Z0-9-]/gi, "")
        .toLowerCase();
};

const getOptionClasses = (
    isChecked: boolean,
    isDisabled: boolean,
): {
    listElementClass: string;
    buttonClass: string;
    inputClass: string;
    labelClass: string;
} => {
    return {
        listElementClass: className({
            [`${BEM_NAME}__list-element--faux-disabled`]: isDisabled,
        }),
        buttonClass: className({
            [`${BEM_NAME}__option`]: true,
            [`${BEM_NAME}__option--selected`]: isChecked,
            [`${BEM_NAME}__option--faux-disabled`]: isDisabled,
        }),
        inputClass: className({
            [`${BEM_NAME}__input`]: true,
            [`${BEM_NAME}__input--faux-disabled`]: isDisabled,
        }),
        labelClass: className({
            [`${BEM_NAME}__label`]: true,
            [`${BEM_NAME}__label--faux-disabled`]: isDisabled,
        }),
    };
};

export const PLCOtherOptionSelector = (
    props: Immutable<{
        categorySelectors: readonly IPLCProductCategorySelector[];
        optionPanels: readonly IPLCOptionPanel[];
        selector: IPLCOtherOptionSelector;
    }>,
): React.ReactNode => {
    const dispatch = useAppDispatch();

    const rootProducts = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.entities.rootProducts;
    });

    const selectedCategories = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return selectedCategoriesSelector(state);
    });

    const optionValues = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.ui.optionValues;
    });

    const value = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return getOptionValueByIdx(
            state.ui.optionValues[props.selector.value.attribute],
            0,
        );
    });

    const handleOptionChange = (optionValue: IOptionSingleValue) => {
        dispatch(
            setOptionValue({
                namespace: "",
                code: props.selector.value.attribute,
                index: 0,
                totalNumValues: 1,
                value: optionValue,
            }),
        );
    };

    const groupID = props.selector.id;

    return (
        <fieldset className={BEM_NAME}>
            <legend>{props.selector.value.name}</legend>
            <ul>
                {props.selector.value.options.map((opt) => {
                    const optionID = generateOptionID(groupID, opt.value);
                    const descriptionID = `${optionID}-description`;
                    const isChecked = value === opt.value;
                    const isDisabled = isOptionDisabled(
                        opt.value,
                        props.categorySelectors,
                        props.optionPanels,
                        rootProducts,
                        selectedCategories,
                        optionValues,
                        props.selector.value.attribute,
                    );
                    const {
                        listElementClass,
                        buttonClass,
                        inputClass,
                        labelClass,
                    } = getOptionClasses(isChecked, isDisabled);

                    return (
                        <li key={opt.name} className={listElementClass}>
                            <div
                                className={buttonClass}
                                onClick={() => {
                                    if (!isChecked) {
                                        handleOptionChange(opt.value);
                                    }
                                }}
                            >
                                <div className={`${BEM_NAME}__icon`}>
                                    {opt.image && (
                                        <img
                                            src={isoImageURL.unwrap(
                                                opt.image.url,
                                            )}
                                            alt={opt.name}
                                        />
                                    )}
                                </div>
                                <input
                                    type="radio"
                                    className={inputClass}
                                    id={optionID}
                                    name={groupID}
                                    onChange={(e) => {
                                        if (e.currentTarget.checked) {
                                            handleOptionChange(opt.value);
                                        }
                                    }}
                                    checked={value === opt.value}
                                    value={opt.value}
                                />
                                <div>
                                    <div>
                                        <label
                                            htmlFor={optionID}
                                            className={labelClass}
                                            aria-selected={false}
                                            aria-describedby={descriptionID}
                                        >
                                            {opt.name}
                                        </label>
                                    </div>
                                    <p
                                        id={descriptionID}
                                        className={`${BEM_NAME}__description`}
                                    >
                                        {opt.description}
                                    </p>
                                </div>
                            </div>
                            <PLCInfoPanel
                                buttonText={"Learn More"}
                                content={opt.help_content}
                            />
                        </li>
                    );
                })}
            </ul>
        </fieldset>
    );
};
